import { TASKBAR_H, Z_BALK, Z_LAAG, Z_VENSTER_MAX } from '../lib/config.js';
import { avatarUrl } from '../lib/pixels.js';
import { iconStroke } from '../lib/icons.js';
import * as tam from '../lib/tamagotchi.js';

/**
 * De paal als bureaublad.
 *
 * Onderin een taakbalk: links wie je bent, in het midden de chat, rechts de bel.
 * Rechtsboven de klok. En naast de paal een kolom met je eigen cijfers en de
 * koppen van iedereen die nu online is.
 *
 * De vorm komt van Windows XP, de kleuren en de lijnen niet. Alles in de balk is
 * gewoon een knop uit deze app: drie pixels rand, harde schaduw, dezelfde roze.
 * Alleen de balk zelf is donker, want anders leest hij als nóg een roze strook
 * onder de roze kop in plaats van als de rand van het scherm.
 */
export default {
  /**
   * Staan je cijfers in beeld?
   *
   * Ze hangen onder de knoppen in de zijbalk links, en die zijbalk is er niet op
   * de telefoon. Meer voorwaarden zijn er niet: ze nemen geen eigen ruimte in en
   * duwen dus ook niets weg.
   */
  showSide() {
      // De paal is het bureaublad en is er altijd, dus die kolom hoort er ook altijd
      // te staan. Hij stond er alleen naast de paal en niet naast het tekenscherm,
      // want dat waren twee schermen die elkaar uitsloten.
      return !this.state.isMobile;
    },

  /**
   * De klok, precies op de minuut.
   *
   * Niet elke seconde kijken of de minuut al om is: dan hertekent de app zich
   * zestig keer per minuut voor niets. Deze zet zijn eigen wekker op de volgende
   * hele minuut en gaat daarna weer slapen.
   */
  tickClock() {
      const nu = new Date();
      const uur = String(nu.getHours()).padStart(2, '0');
      const min = String(nu.getMinutes()).padStart(2, '0');
      const tijd = uur + ':' + min;
      const dagen = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
      const maanden = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
      const datum = dagen[nu.getDay()] + ' ' + nu.getDate() + ' ' + maanden[nu.getMonth()];
      if (tijd !== this.state.klokTijd || datum !== this.state.klokDatum) {
        this.setState({ klokTijd: tijd, klokDatum: datum });
      }
      clearTimeout(this.klokTimer);
      const rest = 60000 - (nu.getSeconds() * 1000 + nu.getMilliseconds());
      this.klokTimer = setTimeout(this.tickClock, rest + 60);
    },

  /**
   * De chat opent vanuit de balk. Klik je op het laatste bericht, dan wil je het
   * gesprek zien; klik je in het invoerveld, dan wil je typen en hoeft er niets
   * open. Dat scheelt een venster voor een berichtje van drie woorden.
   */
  openChatWindow() {
      // Een knop in de taakbalk is een schakelaar: nog een keer klikken doet het
      // venster weer dicht. Maar alleen als hij vooraan ligt; ligt hij achter een
      // ander venster, dan haal je hem naar voren, net als de programmaknoppen (zie
      // vensterUitBalk).
      if (this.state.chatOpen && this.vensterVoorop() === 'chat') { this.setState({ chatOpen: false, emojiOpen: false }); return; }
      this.vensterNaarVoren('chat');
      if (this.state.chatOpen) return;
      this.setState({ chatOpen: true, notifOpen: false, emojiOpen: false }, () => {
        this.markRead(() => requestAnimationFrame(() => this.scrollChat()));
      });
    },

  /**
   * De knop linksonder: open of dicht, en alles wat eroverheen kan liggen gaat
   * dicht.
   *
   * Ook zonder account. Hij was hier een inlogknop, en die sprong meteen naar het
   * accountscherm: je zag de paal, klikte linksonder en kreeg een venster met de
   * vraag wie je bent voordat je iets van de app had gezien. Nu gaat het menu open
   * en kun je rondkijken; inloggen is de eerste regel erin.
   *
   * Wat een account nodig heeft vraagt er zelf om, op het moment dat je het doet.
   * Dat loopt via ensureName in logic/account.js: die opent het accountscherm met
   * een regel erboven die zegt waarvoor.
   */
  toggleStart() {
      const open = !this.state.startOpen;
      this.setState({ startOpen: open, notifOpen: false, emojiOpen: false });
    },

  closeStart() {
      if (this.state.startOpen) this.setState({ startOpen: false });
    },

  /**
   * Een regel uit het startmenu doet twee dingen: het menu gaat dicht en het
   * scherm eronder gaat open. Net als bij een echt startmenu, waar je nooit het
   * menu wil houden dat je net gebruikt hebt.
   */
  startNaar(doe) {
      return () => { this.closeStart(); doe(); };
    },

  /**
   * Je eigen cijfers: hoeveel er van jou aan de paal hangt, hoeveel hartjes dat
   * opleverde en hoe vaak je het thema van de dag hebt gehaald.
   */
  mijnCijfers() {
      const mijn = (this.state.galleryItems || []).filter((g) => this.isMine(g));
      return {
        krabbels: mijn.length,
        hartjes: mijn.reduce((s, g) => s + (g.likesCount || 0), 0),
        themas: mijn.filter((g) => g.isThemeMatch).length
      };
    },

  /**
   * Alles wat de taakbalk en de cijferkolom nodig hebben.
   *
   * Apart gehouden van renderVals, net als pollVals en commentVals: dat bestand is
   * al tweeduizend regels en dit is een eigen hoek van het scherm.
   */
  desktopVals() {
      const mob = this.state.isMobile;
      /**
       * De maat van een pictogram op het bureaublad, twintig procent groter dan
       * waarmee het begon: het vak van 44 naar 53 en de tegel van 76 naar 92.
       */
      const ICOON_VAK = 53;
      const ICOON_TEGEL = 92;
      const ink = 'var(--ink)';
      // De inktlijn boven de taakbalk, en hoever de startknop daar bovenuit steekt.
      // Het startmenu hangt aan datzelfde getal, zodat de twee niet uit elkaar
      // lopen als er ooit aan wordt gedraaid.
      const BALK_LIJN = 4;
      const UITSTEEK = 8;
      const cijfers = this.mijnCijfers();
      // Dezelfde telling als de paginatitel: je eigen berichtjes tellen niet mee.
      const ongelezen = this.unreadFromOthers();
      // De naam die in de balk staat, per venster. Op één plek, want hij staat ook in
      // de titelbalk van het venster en in het startmenu.
      const PROG_TITEL = {
        studio: 'Tekenen', beest: 'Krabbelgotchi', ket: 'Krabbellijn',
        arcade: 'Arcadehal', activity: 'Leaderbord', gastenboek: 'Gastenboek'
      };
      const uur = new Date().getHours();
      const groet = uur < 6 ? 'Nog wakker' : uur < 12 ? 'Goedemorgen' : uur < 18 ? 'Goedemiddag' : 'Goedenavond';

      /**
       * Een knop in het systeemvak: dieproze met een wit tekentje, en een tint
       * lichter zodra zijn venster openstaat.
       *
       * Die dieproze zit ook op de verzendknop een paar pixels ernaast, dus de hele
       * onderrand hangt aan één kleur. Wit stond er eerst en dat leest op een roze
       * balk als een gat in plaats van als een knop; lavendel was te zacht.
       *
       * De open-stand was eerst paars en dat sloeg midden in een roze balk, daarna
       * de lichte roze van de balk zelf en dan viel hij te veel weg. Nu wit: dat is
       * het enige in deze balk dat noch roze noch inkt is, dus je ziet in één oogopslag
       * welk venster openstaat. Het tekentje wordt dan inkt, want wit op wit leest niet.
       */
      // Eén keer uitrekenen: zowel het bolletje bij Arcadehal als de knop in de
      // balk hangen eraan, en dit loopt over alle stappen van alle ketens.
      const ketOpJou = ketBeurtBolletje(this);

      const vak = (aan) => ({
        position: 'relative', flex: '0 0 auto', height: '42px', width: '42px',
        borderRadius: '12px', padding: '0', justifyContent: 'center',
        background: aan ? '#fff' : 'var(--roze)', color: aan ? ink : '#fff'
      });

      /**
       * Dezelfde knop, maar met zijn naam ernaast: een programma dat draait, zoals
       * in de balk van XP. Daar had de klok rechts alleen tekentjes en hadden de
       * knoppen ernaast een naam, en dat onderscheid houden we aan: de chat en de
       * Krabbellijn zijn programma's, de bel is een tekentje in het systeemvak.
       *
       * De maat en het gewicht van de tekst komen van .gp-btn (15px, 600) en de
       * zeven pixels tussen tekentje en tekst ook. Alleen de vaste breedte van
       * tweeënveertig moet eraf en er moet lucht naast, anders staat de naam tegen
       * de rand.
       *
       * Alleen op een groot scherm. Op een telefoon moet de hele balk in 375
       * pixels, met de startknop, het typvak en drie knoppen; daar is het tekentje
       * het enige dat past.
       */
      /**
       * Alle programmaknoppen even breed, en te lange namen met een staartje.
       *
       * `width: auto` liet elke knop zo breed worden als zijn naam, en dan is de balk
       * een rij van ongelijke blokken: "Tekenen" naast "Krabbel van de dag" scheelt
       * bijna het dubbele. Honderdvijftig is de maat van de langste naam die nog
       * helemaal past (Krabbelgotchi, nagemeten 89 pixels tekst plus tekentje, gat en
       * padding); wat langer is eindigt in `…`. Zie progNaamStyle hieronder, want dat
       * afkappen moet op de tekst zelf en niet op de knop: een knop is een flexrij en
       * die knijpt zijn tekentje plat in plaats van de letters af te kappen.
       */
      const PROG_BREED = 150;
      const vakNaam = (aan) => Object.assign(vak(aan), { width: PROG_BREED + 'px', padding: '0 13px' });

      // Staan de namen aan? Zo ja dan zijn de knoppen breed, zo nee dan zijn het
      // vierkantjes van 42 net als de bel. Eén vlag voor allebei, want een brede
      // knop zonder tekst is een knop met een gat erin.
      /**
       * Welke programma's er in de balk staan, en of hun naam erbij past.
       *
       * De regel voor "staat hij erin" is voor alle zeven hetzelfde: een balkje betekent dat het programma draait. Het kruisje in de titelbalk stopt
       * het en haalt het balkje weg; de wegklapknop ernaast laat het staan. Zie
       * vensterLijst in logic/venster.js.
       */
      const progLijst = this.vensterLijst()
        // De chat hangt in het systeemvak en hoort niet bij de programma's.
        .filter((w) => w.erin && w.naam !== 'chat' && !(mob && w.naam !== 'beest' && w.naam !== 'ket'))
        .map((w) => Object.assign({
          id: w.naam, titel: PROG_TITEL[w.naam] || w.naam, aan: w.open,
          onClick: () => this.vensterUitBalk(w.naam)
        }, w.naam === 'beest' ? { stip: tamRoept(this) }
          : w.naam === 'ket' ? { stip: !!ketOpJou } : null));

      /**
       * Staan de namen erbij? Dat hangt af van hoeveel knoppen er staan.
       *
       * Een knop met naam is ongeveer 150 pixels breed, een tekentje 42. Naast de
       * balk gaan de startknop (tot 220) en het systeemvak met de klok (ruim 170)
       * eraf, plus de gaten. Passen de namen niet allemaal, dan gaan ze allemaal uit:
       * een rij van half brede en half vierkante knoppen leest als een storing.
       *
       * Hier stond alleen de grens van narrowHeader (940). Die was gemaakt voor drie
       * knoppen; met zeven liep de rij het systeemvak in.
       */
      const progRuimte = (typeof window === 'undefined' ? 1200 : window.innerWidth) - 430;
      // Alleen de ruimte beslist. Hier stond ook nog `narrowHeader` (onder de 940), en
      // dan stond er op een laptopvenster een los potloodje waar plek was voor "Tekenen".
      const metNaam = !mob
        && progLijst.length > 0 && progRuimte / progLijst.length >= PROG_BREED + 10;

      /**
       * Eén cijfer: het getal, en waar het over gaat.
       *
       * Geen tekentje ervoor. Eerst stonden hier drie gekleurde tegels, daarna drie
       * emoji, en allebei bleven onrustig: drie verschillende plaatjes onder elkaar
       * met elk hun eigen vorm en kleur trekken meer aandacht dan de getallen zelf.
       * Dit is een bijschrift bij de paal, geen dashboard.
       */
      const rij = (getal, label) => ({ getal: String(getal), label });

      /**
       * Eén regel in het startmenu: gekleurd vakje, en waar hij heen gaat.
       *
       * Zonder ondertitel. Zes regels met elk een zin eronder maakten van een menu
       * een lap tekst, en "Verzoekjes van en aan collega's" vertelt je niets meer
       * zodra je één keer op Postvakje hebt geklikt.
       */
      /** De rij zelf; één regel mag een eigen vulling hebben. Zie Krabbelworm. */
      const rijBasis = {
        display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left',
        borderRadius: '12px', padding: '7px 9px', fontWeight: 600
      };
      const menuItem = (icoon, kleur, titel, onClick, badge, vulling) => ({
        icoon, titel, onClick, badge: badge || '',
        rijStyle: vulling ? Object.assign({}, rijBasis, { background: vulling }) : rijBasis,
        vakStyle: {
          width: '32px', height: '32px', flex: '0 0 auto', border: '3px solid ' + ink,
          borderRadius: '12px', background: kleur, display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1
        }
      });

      const items = [];
      /**
       * De bovenste regel: naar binnen, of naar iedereen.
       *
       * Dit waren twee regels boven elkaar: "Inloggen" met een sleuteltje voor wie
       * nog niemand was, en daaronder "Iedereen hier" met een smiley. Dat is één
       * regel te veel, want beide gaan naar hetzelfde scherm en de bovenste vertelt
       * alleen wat je daar gaat doen.
       *
       * Nu is het één regel die zijn naam aanpast. De smiley blijft staan: het
       * tekentje hoort bij de plek en niet bij wat je er komt doen, en dan hoort
       * hij niet van sleutel naar gezicht te wisselen omdat je bent ingelogd.
       *
       * De kleur wisselt wel. Inloggen is het enige in dit menu dat er niet is om
       * te kijken maar om binnen te komen, en dat is de roze van een hoofdactie.
       */
      const binnen = !this.state.accountId;
      items.push(
        // "Mijn account" stond hier ook. Daar kom je al via "Iedereen hier": je
        // eigen regel staat bovenaan en die klikt door naar je eigen account. Twee
        // wegen naar hetzelfde scherm in één menu van zes regels is er één te veel.
        menuItem('🙂', binnen ? 'var(--roze)' : 'var(--lila-diep)',
          binnen ? 'Inloggen' : 'Iedereen hier',
          this.startNaar(binnen ? this.openAccount : this.openIedereen))
      );
      // Hier stond Tekenen. Het pictogram op het bureaublad is de weg naar het
      // tekenvenster, en zijn balkje in de taakbalk de weg terug; een derde deur in
      // het startmenu was er een te veel.
      items.push(
        menuItem('✉️', 'var(--roze-balk)', 'Postvakje', this.startNaar(this.openInbox), ongelezenVerzoeken(this)),
        menuItem('💡', 'var(--blauw)', 'Ideeënbus', this.startNaar(this.requestFeature),
          nieuweIdeetjes(this)),
        // Eén regel naar de hal in plaats van vier naar de spelletjes. Dit menu
        // werd anders een lijst van acht waarvan de helft een spelletje was, en
        // elk volgend spelletje zou er weer een regel bij zijn.
        // Ligt er een beurt op je te wachten, dan zegt het bolletje dat in plaats
        // van "Nieuw!": een openstaande beurt is dringender dan nieuws.
        menuItem('🕹️', '#fff', 'Arcadehal', this.startNaar(this.openArcade),
          ketOpJou || 'Nieuw!', 'var(--lila-diep)')
      );
      /**
       * Paal opruimen zat als brede knop in de kolom naast de paal.
       *
       * Die kolom is opgeruimd, maar dit is de enige weg terug als je krabbels hebt
       * verhangen en het een rommeltje is geworden. Geen pictogram, want het is geen
       * programma dat opengaat maar een handeling, en hij staat er alleen als er echt
       * iets verhangen is.
       */
      if (this.wallIsArranged()) {
        items.push(menuItem('↩️', '#fff', 'Paal opruimen', this.startNaar(this.resetWall)));
      }
      // "Wat is er nieuw?" hing aan het logo, en dat staat niet meer op het
      // bureaublad. Zonder deze regel was het welkomstvenster onbereikbaar.
      items.push(menuItem('🎁', 'var(--geel-vlak)', 'Wat is er nieuw?', this.startNaar(this.openWelcome)));
      /**
       * De tekenaar staat in het menu, voor iedereen.
       *
       * Hij zat eerst in het modgereedschap en daarna achter het betavinkje, en dat
       * hoorde bij hoe de ontwerpen toen werkten: de vijf soorten waren van iedereen
       * samen, dus een oog bijwerken keek alle beestjes van dat soort anders aan. Nu
       * heeft elk beestje zijn eigen tekening en raak je met een oog alleen je eigen
       * beestje. Alleen de spullenbak binnenin is nog voor de mod en de testers.
       *
       * Zonder account kun je niets bewaren; dat vraagt het scherm zelf zodra je het
       * opendoet. Zie openBeestje in logic/beestje.js.
       */
      items.push(menuItem('🎨', 'var(--groen)', 'Beestje ontwerpen', this.startNaar(this.openBeestje)));
      if (this.isMaintainer) {
        items.push(menuItem('🔧', 'var(--ink)', 'Modgereedschap', this.startNaar(this.openMod)));
      }

      return {
        taskbarH: TASKBAR_H,

        // Alle tekentjes in de balk op één maat en één lijndikte, net zoals de
        // gereedschapskolom dat doet. De dikte volgt uit de maat: elk tekentje is
        // getekend op een raster van 24, dus om overal dezelfde 2,2 pixels op het
        // scherm te krijgen moet de opgegeven dikte meeschalen. Zet je de maat
        // anders, dan rekent de dikte zichzelf om.
        //
        // Twintig in een knop van 42: bijna de helft van de knop. Er stond een tweede
        // maat naast (`barIcon`, vijftien) voor de knopjes in de chatbalk, en die balk
        // is er niet meer.
        trayIcon: 20,
        trayStroke: iconStroke(20),

        // ---- de taakbalk ------------------------------------------------
        taskbarStyle: {
          position: 'fixed', left: 0, right: 0, bottom: 0,
          // Dezelfde roze als de kop. Boven en onder dezelfde rand, de room
          // ertussen is het werkvlak. De knoppen erin zijn lavendel, want wit
          // leest op roze als een gat in plaats van als een knop.
          background: 'var(--roze-balk)',
          // Vier pixels, net als de lijn onder de kop. Drie tegen vier zie je wel
          // degelijk als de twee balken tegenover elkaar staan.
          borderTop: '4px solid ' + ink,
          // Boven de programmaatjes. Zie Z_BALK in lib/config.js: een venster dat over
          // de balk heen lag sneed de knoppen erin af. Op de telefoon niet, want daar
          // dekken de vensters de balk met opzet af; ze zijn er schermvullend.
          zIndex: mob ? 70 : Z_BALK, display: 'flex', alignItems: 'center',
          // Onderaan meer lucht dan bovenaan: de knoppen stonden met negen pixels te
          // dicht op de schermrand. En op een iPhone komt daar de strook van de
          // home-balk bij: env() is nul op alles wat die niet heeft, dus daar
          // verandert er niets. De balk groeit dan mee met die strook, zodat de
          // knoppen erboven blijven staan in plaats van eronder te verdwijnen.
          height: 'calc(' + TASKBAR_H + 'px + env(safe-area-inset-bottom, 0px))',
          paddingTop: '2px',
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          // Rechts ruimer dan links: links loopt de startknop met opzet van het
          // scherm af, rechts staan losse knoppen en die hoorden niet tegen de rand
          // te plakken. Acht pixels was te weinig zodra de klok daar niet meer stond.
          paddingLeft: mob ? '8px' : '16px', paddingRight: mob ? '14px' : '16px',
          gap: mob ? '7px' : '10px'
        },

        /**
         * De programma's staan links, meteen naast de startknop.
         *
         * Dat is waar een taakbalk zijn draaiende programma's zet, en het is ook
         * waar ze horen: links begint het, rechts zit het systeemvak. Ze stonden
         * allemaal in dat vak rechts, en dan schoof een venster erbij de bel en de
         * klok elke keer een stuk naar links.
         *
         * `1 1 auto` met `minWidth: 0`: op een smal bureaublad mag deze groep krimpen
         * in plaats van het systeemvak van de rand af te duwen.
         *
         * En geen `overflow: hidden`. Dat stond er wel, als vangnet voor als er ooit
         * meer knoppen bij komen dan er passen, en het knipte elke dag iets af wat er
         * wél moest staan: deze groep is precies zo hoog als een knop (42) en de
         * schaduw van `.gp-btn` valt drie pixels naar beneden. Die viel dus buiten de
         * groep en werd weggeknipt, waardoor elke programmaknop onderaan afgesneden
         * leek. Er is dertien pixels ruimte onder in de balk, dus er valt niets te
         * klemmen; het vangnet loste een probleem op dat er niet was.
         *
         * Overloopt de groep ooit echt, dan schuiven de knoppen zichtbaar onder het
         * systeemvak en zie je dat meteen. Dat is beter dan een schaduw die er voor
         * iedereen permanent afligt. De namen gaan trouwens onder 940 pixels al uit
         * (zie metNaam hierboven), en dan zijn het vierkantjes van 42.
         *
         * De schaduw hoort ook niet tegen de onderrand van de balk aan te liggen:
         * `marginBottom` tilt de hele groep vier pixels op, zodat er onder de schaduw
         * even veel lucht staat als erboven.
         */
        progStyle: {
          flex: '1 1 auto', minWidth: '0',
          display: 'flex', alignItems: 'center', gap: mob ? '7px' : '10px',
          marginBottom: '4px'
        },

        // De startknop: jij.
        toggleStart: this.toggleStart,
        startOpen: this.state.startOpen,
        /**
         * De startknop vult de hele hoogte van de balk en zit vast in de hoek van
         * het scherm.
         *
         * Hij was een losse knop van 42 tussen de andere, en dan is het gewoon de
         * eerste van vijf. Een startknop is geen knop tussen de knoppen: hij is de
         * hoek waar je begint. Vandaar de volle hoogte, geen rondingen aan de
         * linkerkant, en een negatieve marge die de padding van de balk opeet zodat
         * hij het scherm echt raakt.
         *
         * Hij steekt acht pixels boven de balk uit en loopt naar links en naar onder
         * van het scherm af. Aflopend dus: geen rand en geen ronding waar hij de
         * schermrand raakt, want daar houdt hij niet op, daar houdt het scherm op.
         * Alleen boven en rechts een lijn, met de hoek daartussen rond.
         *
         * De schaduw komt van .gp-btn zelf en staat hier dus niet in de stijl. Dat is
         * met opzet: een inline-schaduw wint van de CSS, en dan kunnen :hover en
         * :active hem niet meer veranderen. Precies dat had ik eerst gedaan, en toen
         * groeide de schaduw niet bij aanwijzen en verdween hij niet bij indrukken.
         * Naar onderen valt die schaduw buiten het scherm, dus je ziet de diepte aan
         * de rechterkant. Dat is genoeg: zonder schaduw zag hij eruit alsof hij al
         * ingedrukt was.
         *
         * Omdat hij een gewone schaduw heeft, doet hij ook de gewone knopanimatie:
         * opwippen bij aanwijzen, wegzakken bij indrukken. Eén ding gaat anders, en
         * dat staat in .gp-hoek in styles.css: het krimpen dat bij :active hoort is
         * hier weggelaten, want op deze maat overstemt dat de verschuiving.
         *
         * Wel loopt hij tien pixels verder door dan het scherm, links en onder. Die
         * animatie verschuift hem niet alleen, hij krimpt ook een procentje, en dan
         * komt de linkerkant van de schermrand af. Aan die kant zit geen rand, dus
         * je zag bij elke klik even een afgesneden kant oplichten. Tien pixels reserve
         * is meer dan die beweging ooit nodig heeft. De padding aan die kant is met
         * dezelfde tien meegegroeid, zodat het kopje op zijn plek blijft staan. Naar
         * beneden loopt hij om dezelfde reden door tot voorbij de schermrand.
         *
         * Onderaan zit ruim padding om diezelfde reden andersom: de knop is tien
         * pixels hoger dan wat je ziet, en zonder die padding staat je naam netjes
         * in het midden van de knop maar te laag in het stukje dat in beeld is. Nu
         * staat hij daar iets boven het midden, dicht bij de rand die uitsteekt.
         */
        startBtnStyle: {
          alignSelf: 'stretch', flex: '0 0 auto', boxSizing: 'border-box',
          marginLeft: mob ? '-18px' : '-26px', marginTop: -(UITSTEEK + BALK_LIJN) + 'px',
          // Tien voor de onderrand van de balk, tien om er echt doorheen te lopen, en
          // op een iPhone de home-strook erbij. Zonder die eerste tien stopte de knop
          // precies op de schermrand en viel zijn schaduw van drie pixels er net
          // binnen: een dun zwart streepje onderlangs.
          marginBottom: 'calc(-20px - env(safe-area-inset-bottom, 0px))',
          padding: mob ? '0 12px 20px 20px' : '0 18px 22px 22px',
          gap: '11px', fontSize: mob ? '14px' : '15px', fontWeight: 700,
          maxWidth: mob ? '128px' : '220px', overflow: 'hidden',
          border: '3px solid ' + ink, borderLeft: 'none', borderBottom: 'none',
          borderRadius: '0 16px 0 0',
          background: this.state.startOpen ? '#fff' : 'var(--roze)',
          color: this.state.startOpen ? ink : '#fff'
        },
        startAvatarStyle: {
          width: '34px', height: '34px', flex: '0 0 auto', imageRendering: 'pixelated',
          border: '3px solid ' + ink, borderRadius: '12px', background: '#fff', display: 'block'
        },
        // Het vakje met een vraagteken, zolang je nog geen kopje hebt.
        //
        // Geen eigen kleur voor dat vraagteken: het erft die van waar het staat. In de
        // startknop is dat wit op dieproze, en dat is waar bijna iedereen het ziet; het
        // grijs dat er stond viel daar half weg tegen de roze. In de kop van het
        // startmenu, op lichtroze, erft het de inkt en blijft het leesbaar. Eén regel
        // voor twee plekken die elkaars tegenpool zijn.
        startLeegStyle: {
          width: '34px', height: '34px', flex: '0 0 auto', border: '3px dashed ' + ink,
          borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '17px', fontWeight: 700
        },
        // Zolang je niemand bent staat hier "Start" en niet "Log in". Inloggen is
        // wat er achter deze knop gebeurt als je het wil, niet wat de knop doet:
        // hij opent het menu, en de eerste regel daarin is de deur naar binnen.
        startNaam: this.state.author || 'Start',
        startTitel: this.state.author ? 'Jouw menu' : 'Het startmenu',
        // Op de telefoon staat er normaal alleen een kopje in de knop; de balk is
        // daar te smal voor een naam erbij. Zolang je niemand bent moet "Start"
        // er wel staan: een vraagteken alleen vertelt niet dat je erop kunt
        // klikken.
        showStartNaam: !mob || !this.state.author,
        myAvatar: avatarUrl(this.state.avatar),
        // Ook een account nodig, niet alleen een plaatje. De avatar blijft in de
        // browser staan nadat je uitlogt, en dan stond er een kop naast "Start"
        // van iemand die er niet meer is.
        heeftAvatar: !!this.state.accountId && !!(this.state.avatar && /[0-9a-f]/.test(this.state.avatar)),

        /**
         * Hier stond de chatbalk: emoji, tekstveld, buzzer, versturen, midden in de
         * taakbalk, zodat je kon typen zonder een venster open te doen.
         *
         * Hij is eruit. Hij nam het hele midden in beslag en duwde de knoppen ernaast
         * in de verdrukking zodra er een programma bij kwam, en alles wat erin zat
         * staat ook onderin het chatvenster: het veld, de tekentjes, de buzzer en de
         * verzendknop. De knop naar dat venster staat rechts in het systeemvak.
         *
         * Dat stond hier eerst als `showChatBar: false` met de hele balk er nog onder:
         * honderdveertig regels stijl en zestig regels JSX die nooit meer aan de beurt
         * kwamen. Uitgezet is niet weg, en code die niet loopt gaat verouderen zonder
         * dat iemand het merkt.
         */

        // ---- het systeemvak rechts --------------------------------------
        // Altijd tegen de rechterrand. De chatbalk in het midden duwde het
        // systeemvak vroeger vanzelf die kant op; nu die weg is doet deze marge
        // het, op de telefoon net zo goed als op een computer.
        // Even hoog als de programmaknoppen links: die staan vier pixels opgetild
        // (progStyle), en zonder dezelfde marge hier stonden de chat, de bel en de klok
        // twee pixels lager dan de rest van de balk.
        trayStyle: { flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: mob ? '6px' : '8px', marginLeft: 'auto', marginBottom: '4px' },
        // Allebei wit, en lichtroze zodra het venster dat eraan hangt openstaat. Zo
        // zegt de kleur iets in plaats van dat het decoratie is, en zie je aan de
        // balk wat er open is zonder ernaar te zoeken.
        //
        // Er stond een derde knop: het modgereedschap. Die is er voor precies één
        // persoon en stond bij die persoon permanent in de balk, naast twee knoppen
        // die iedereen heeft. Nu staat hij waar de andere zeldzame dingen staan:
        // in het startmenu.
        trayBtnStyle: vak(this.state.notifOpen),
        /**
         * De chat is een tekentje, geen programmaknop.
         *
         * Hij stond met zijn naam ernaast tussen de programma's, en dat is precies
         * de plek die hij niet heeft: de chat hoort bij de bel. Twee dingen die
         * altijd meekijken en waar je nooit naartoe hoeft. De programma's staan
         * links, hun naam erbij; het systeemvak rechts, alleen tekentjes.
         */
        chatWinBtnStyle: vak(this.state.chatOpen),
        /*
         * Hier stonden drie blokken uitleg over de knoppen van de Krabbellijn, de
         * Krabbelgotchi en het tekenvenster in de balk: elk met hun eigen regel voor
         * wanneer ze er staan. Die regel is nu voor alle zeven dezelfde en staat bij
         * `progLijst` hierboven en bij `vensterLijst` in logic/venster.js.
         *
         * Wat er uit die blokken overeind blijft: de Krabbellijn staat er ook als het
         * venster dicht is zolang er een beurt op je ligt, met hetzelfde roze stipje
         * als bij de bel, en hij staat er alleen voor wie het spel mag openen.
         */
        /**
         * De programma's op het bureaublad, linksboven in de hoek.
         *
         * Eén klik opent ze. Dat is dezelfde afspraak als de rest van de app: overal
         * één klik. Het was even dubbelklikken omdat dat op een bureaublad gewoon is,
         * maar twee afspraken naast elkaar in dezelfde app is er één te veel.
         *
         * Hier staat ook wat eerst als brede knop in de kolom naast de paal stond: de
         * krabbel van de dag, het gastenboek en het leaderbord. Het is allemaal iets
         * dat opengaat, dus het hoort bij de rest.
         *
         * De chat staat er niet bij. Die zit rechtsonder in het systeemvak, altijd
         * op dezelfde plek en met het aantal ongelezen berichten erop; een tweede
         * deur ernaartoe in de hoek linksboven is er een te veel.
         */
        showBureaubladIconen: !mob,
        bureaubladIconen: [
          { naam: 'Tekenen', teken: '🖌️', kleur: 'var(--geel)', open: this.openStudio },
          { naam: 'Arcadehal', teken: '🕹️', kleur: 'var(--lila-diep)', open: this.openArcade },
          // De Krabbelgotchi staat er voor iedereen; hij zat achter het betavinkje en
          // is eruit. Binnen de tekenaar is alleen de spullenbak nog voor de testers.
          { naam: 'Krabbelgotchi', teken: '👾', kleur: 'var(--blauw)', open: this.openTamagotchi },
          // Deze twee laten zien wat erin zit in plaats van een tekentje: de krabbel
          // van vandaag en het gastenboekvel zoals het er nu uitziet. Een voorbeeldje
          // zegt meer dan een sterretje, en het verandert elke dag.
          { naam: 'Krabbel van de dag', preview: 'dag', teken: '🌟', kleur: 'var(--roze)', open: this.openDaily },
          { naam: 'Gastenboek', preview: 'gastenboek', teken: '📖', kleur: 'var(--geel-vlak)', open: this.openGuestbook },
          { naam: 'Leaderbord', teken: '🏆', kleur: '#fff', open: this.openActivity }
        ].map((p2) => ({
          naam: p2.naam,
          teken: p2.teken,
          preview: p2.preview || '',
          titel: p2.naam + ' openen',
          onClick: p2.open,
          tegelStyle: {
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px',
            width: ICOON_TEGEL + 'px', padding: '7px 4px', boxSizing: 'border-box',
            border: 'none', background: 'none',
            borderRadius: '12px', cursor: 'pointer', textAlign: 'center'
          },
          vakStyle: {
            width: ICOON_VAK + 'px', height: ICOON_VAK + 'px', flex: '0 0 auto',
            border: '3px solid var(--ink)', overflow: 'hidden',
            borderRadius: '12px', background: p2.kleur, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: Math.round(ICOON_VAK * 0.5) + 'px', lineHeight: 1
          },
          naamStyle: {
            fontSize: '13px', fontWeight: 700, lineHeight: 1.2, color: 'var(--ink)',
            width: '100%', wordBreak: 'break-word'
          }
        })),
        // Twee kolommen, en niet zoveel als er passen: met drie op een rij liep de rij
        // de hele kolombreedte door en werd het een balk in plaats van een hoekje.
        bureaubladRasterStyle: {
          display: 'grid', gridTemplateColumns: 'repeat(2, ' + ICOON_TEGEL + 'px)',
          gap: '4px', paddingTop: '4px', justifyContent: 'start'
        },

        /**
         * De programma's in de balk, als lijst.
         *
         * Er stonden drie knoppen met de hand uitgeschreven, elk met hun eigen
         * `show`-vlag, hun eigen stijl en hun eigen tekentje in de JSX. Bij drie kan
         * dat; bij zeven wordt het zeven keer dezelfde zes regels, en dan loopt er
         * één uit de pas. Nu is het één lijst en zet de balk hem neer.
         *
         * Wanneer een programma erin staat, staat bij progLijst hierboven.
         *
         * `aan` is lichtroze wanneer het venster openstaat, zodat de kleur iets zegt
         * in plaats van decoratie te zijn. `stip` is het roze bolletje dat ook bij de
         * bel staat: er ligt iets op je te wachten.
         */
        /**
         * De programma's in de balk. De lijst staat boven bij progLijst; hier komt
         * alleen de vormgeving erbij.
         *
         * `aan` is lichtroze wanneer het venster openstaat, zodat de kleur iets zegt
         * in plaats van decoratie te zijn. `stip` is hetzelfde roze bolletje als bij
         * de bel: er ligt iets op je te wachten.
         */
        progKnoppen: progLijst.map((k) => ({
          id: k.id, titel: k.titel, naam: metNaam ? k.titel : '',
          stip: !!k.stip, onClick: k.onClick,
          style: metNaam ? vakNaam(k.aan) : vak(k.aan)
        })),
        // Het afkappen zit op de tekst en niet op de knop; zie PROG_BREED hierboven.
        progNaamStyle: {
          minWidth: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        },
        openChatWindow: this.openChatWindow,
        showBarBadge: !this.chatVisible() && ongelezen > 0,
        barBadge: ongelezen > 20 ? '20+' : String(ongelezen),
        barBadgeStyle: {
          position: 'absolute', top: '-7px', right: '-7px', minWidth: '20px', height: '20px',
          padding: '0 5px', borderRadius: '12px', background: 'var(--paars)', color: '#fff',
          border: '3px solid ' + ink, fontSize: '11px', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        },

        // De klok. Rechtsonder in het vak, en met opzet geen knop: er valt niets te
        // klikken aan hoe laat het is.
        klokTijd: this.state.klokTijd,
        klokDatum: this.state.klokDatum,
        // Niet op de telefoon. Daar staat de tijd al bovenaan het scherm, in de
        // balk van de telefoon zelf, en een tweede klok twee centimeter lager is
        // geen informatie maar ruis. Wat het wel kostte: de zeventig pixels waar
        // hij stond duwden de knoppen van de rechterrand af. Nu staan ze rechts.
        showKlok: !mob,
        klokStyle: {
          flex: '0 0 auto', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', minWidth: '62px',
          marginLeft: '3px', padding: '0 2px 0 15px', color: ink, lineHeight: 1,
          borderLeft: '2px solid rgba(36,27,58,.2)', height: '30px'
        },
        klokTijdStyle: { fontSize: '14px', fontWeight: 700, letterSpacing: '.02em' },
        klokDatumStyle: { fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', marginTop: '3px' },

        // ---- het startmenu ----------------------------------------------
        // Uitgelijnd op de startknop: linkerkant tegen dezelfde schermrand. Eerst
        // stond het menu twaalf pixels van de rand en vier pixels boven de knop,
        // uitgelijnd op niets.
        //
        // Verticaal blijft er wel lucht. De schaduw van het menu is zeven pixels en
        // valt naar beneden; zette ik de onderkant precies op de knop, dan lag die
        // schaduw als een donkere streep over de roze balk en over de startknop
        // heen. Tien pixels erboven laat hem netjes op de room vallen.
        //
        // Op de telefoon niet. Daar is het menu een venster als alle andere:
        // schermvullend, met de donkere paalkleur eromheen. Die kleur komt hier van
        // een schaduw van 100vmax in plaats van een aparte laag eronder, dezelfde
        // truc als bij het chatvenster en de bel — anders zou dit venster als enige
        // een extra laag in de boom nodig hebben. Sluiten doe je met het kruisje in
        // de kop; ernaast tikken kan niet meer, want er is geen ernaast.
        // De laag waarop "ernaast klikken" landt. Op een groot scherm ligt die net
        // onder de taakbalk, zodat je vanuit het menu meteen op een knop in de balk
        // kunt klikken. Op de telefoon dekt het menu de balk af en moet die laag er
        // juist boven liggen, anders tik je in de rand naast het venster op een knop
        // die je niet ziet.
        startBackStyle: { position: 'fixed', inset: 0, zIndex: mob ? Z_BALK : Z_VENSTER_MAX },
        startMenuStyle: mob
          ? {
              // Schermvullend op de telefoon, dus in de laag van de schermvullende
              // vensters. Op 80 lag hij onder een open programmaatje (90 tot 95).
              position: 'fixed', inset: '10px', zIndex: Z_LAAG,
              background: '#fff', border: '4px solid ' + ink, borderRadius: '16px',
              boxShadow: '0 0 0 100vmax ' + ink, display: 'flex', flexDirection: 'column',
              overflow: 'hidden', overscrollBehavior: 'contain'
            }
          : {
              position: 'fixed', left: '0', bottom: (TASKBAR_H + UITSTEEK + 10) + 'px',
              // Eén kolom knoppen, dus geen zevenhonderd meer nodig; de lijst met
              // iedereen zat rechts en is nu een knop in dat rijtje.
              width: 'min(336px, calc(100vw - 12px))',
              // dvh en niet vh. Op de telefoon is 100vh de hoogte zonder adresbalk, en
              // die balk staat er wel: het scherm zelf gebruikt 100dvh (Template.jsx).
              // Met vh werd het menu tot 78 pixels hoger dan het zichtbare vlak, en wat
              // eraf viel was zijn eigen kop met de groet en het kruisje. Die kop staat
              // buiten het scrollgebied, dus je kon er niet naartoe.
              maxHeight: 'calc(100dvh - ' + (TASKBAR_H + UITSTEEK + 32) + 'px)',
              background: '#fff', border: '3px solid ' + ink, borderRadius: '16px',
              boxShadow: '7px 7px 0 ' + ink, zIndex: Z_BALK, display: 'flex', flexDirection: 'column', overflow: 'hidden'
            },
        // De roze kop met je naam erin, zoals de titelbalk van elk venster hier.
        startKopStyle: {
          flex: '0 0 auto', background: 'var(--roze-balk)', borderBottom: '3px solid ' + ink,
          // Links 21, zodat het kopje in de kop op dezelfde rand staat als de vakjes van
          // de regels eronder (rand 3, lijst 12, regel 9: 24). Op 14 stond de kop zeven
          // pixels verder naar links dan alles eronder.
          padding: '12px 14px 12px 21px', display: 'flex', alignItems: 'center', gap: '11px'
        },
        // Padding erop en een even grote negatieve marge eromheen: het raakvlak wordt
        // 44 zonder dat het kruisje een pixel verschuift. Op de telefoon is dit naast
        // een tik naast het menu de enige uitgang, en de strook waar je "naast" kunt
        // tikken is daar maar achttien pixels hoog.
        startSluitStyle: {
          marginLeft: 'auto', flex: '0 0 auto', border: 'none', background: 'transparent',
          cursor: 'pointer', color: ink, display: 'flex',
          padding: '12px', margin: '-12px -12px -12px auto', borderRadius: '12px'
        },
        startKopAvatarStyle: {
          width: '42px', height: '42px', flex: '0 0 auto', imageRendering: 'pixelated',
          border: '3px solid ' + ink, borderRadius: '12px', background: '#fff', display: 'block'
        },
        startGroet: groet,
        startGroetStyle: { fontSize: '12px', fontWeight: 700, color: 'var(--roze-ink)' },
        startNaamGroot: this.state.author || 'Nog niemand',
        startNaamGrootStyle: { fontSize: '19px', fontWeight: 700, lineHeight: 1.2 },
        startBodyStyle: {
          flex: '1 1 auto', minHeight: '0', display: 'flex',
          flexDirection: 'column', gap: '12px', padding: '12px', overflowY: 'auto'
        },
        startLinksStyle: { flex: '1 1 auto', minWidth: '0', display: 'flex', flexDirection: 'column', gap: '7px' },
        startItems: items,
        startItemTitelStyle: { fontSize: '15px', fontWeight: 700 },
        startItemBadgeStyle: {
          flex: '0 0 auto', minWidth: '15px', height: '19px', padding: '0 5px', borderRadius: '12px',
          background: 'var(--paars)', color: '#fff', border: '2px solid ' + ink, fontSize: '11px',
          fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center'
        },
        // De voetbalk, waar bij XP "Log Off" stond. Sluiten doe je met het kruisje
        // in de kop, zoals bij elk ander venster hier; die knop hoefde er dus niet
        // ook nog eens onderaan bij.
        //
        // Links loopt nu de nieuwsregel. Die zat in de hal, en toen de hal opging in
        // dit menu bleef die code achter zonder dat er nog iets van te zien was. Hier
        // vult hij de lege helft van de balk met iets dat leeft.
        startVoetStyle: {
          flex: '0 0 auto', background: 'var(--lila)', borderTop: '3px solid ' + ink, padding: '9px 12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
        },
        // Het spoor is twee keer dezelfde lijst achter elkaar en schuift precies de
        // helft op. Daardoor loopt hij rond zonder zichtbaar begin of eind; zie
        // .gp-ticker in styles.css.
        tickerWrapStyle: { flex: '1 1 auto', minWidth: '0', overflow: 'hidden' },
        tickerRailStyle: { display: 'flex', width: 'max-content' },
        tickerItemStyle: {
          display: 'flex', alignItems: 'center', gap: '7px', padding: '0 18px',
          fontSize: '12px', fontWeight: 600, color: 'var(--tekst-2)', whiteSpace: 'nowrap'
        },
        tickerIconStyle: { fontSize: '14px', lineHeight: 1 },
        startUitStyle: { height: '36px', borderRadius: '12px', fontSize: '13px', padding: '0 13px' },
        // Alleen als er iets is om uit te loggen. De weg naar binnen staat bovenaan
        // in de lijst, als eerste menuregel: een roze regel tussen de andere valt
        // op waar een knopje van dertien pixels in de voet dat niet doet, en twee
        // deuren naar hetzelfde scherm maken van de een een omweg.
        showStartUit: !!this.state.accountId,
        startUitloggen: () => { this.closeStart(); this.confirmLogout(); },
        closeStart: this.closeStart,

        // ---- je eigen cijfers, onder de knoppen links --------------------
        // Geen wit vak eromheen. Drie getallen zijn geen venster; die mogen gewoon
        // op de achtergrond liggen, net als de koppen rechtsboven.
        showSide: this.showSide(),
        sideColStyle: { display: 'flex', flexDirection: 'column', gap: '9px', padding: '8px 2px 0' },
        sideCardStyle: { display: 'flex', flexDirection: 'column', gap: '4px' },
        sideKopStyle: { fontSize: '11px', fontWeight: 700, color: 'var(--tekst-2)', letterSpacing: '.05em', textTransform: 'uppercase' },
        statRijStyle: { display: 'flex', alignItems: 'baseline', gap: '10px' },
        // Links uitgelijnd, in een kolom van vaste breedte met cijfers van gelijke
        // breedte. Zo begint alles op dezelfde lijn als het kopje erboven én
        // beginnen alle woorden op dezelfde plek. Rechts uitlijnen deed dat tweede
        // ook, maar duwde de getallen weg van de linkerrand en dat gaf het hele
        // blokje een rafelige kant.
        statGetalStyle: {
          fontSize: '15px', fontWeight: 700, lineHeight: 1.4, minWidth: '26px',
          textAlign: 'left', fontVariantNumeric: 'tabular-nums'
        },
        statLabelStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-2)' },
        // Op account-id, niet op naam. Elke krabbel van na de accounts draagt een
        // accountId, en isMine vergelijkt dan alleen daarop. Zonder account zou de
        // kaart dus altijd drie nullen laten zien, en dat is geen cijfer maar een
        // teleurstelling.
        heeftAccount: !!this.state.accountId,
        statRijen: [
          rij(cijfers.krabbels, cijfers.krabbels === 1 ? 'krabbel' : 'krabbels'),
          rij(cijfers.hartjes, cijfers.hartjes === 1 ? 'hartje' : 'hartjes'),
          rij(cijfers.themas, cijfers.themas === 1 ? 'thema gehaald' : "thema's gehaald")
        ],
        sideLoginStyle: { fontSize: '13px', fontWeight: 600, color: 'var(--tekst-2)', lineHeight: 1.4 }
      };
    }
};

/**
 * Ligt er een beurt van de Krabbellijn op je te wachten?
 *
 * Eén bolletje, geen getal: je hebt hoogstens één lopende beurt tegelijk, want een
 * beurt is aan jou gericht en duurt een kwartier.
 */
function ketBeurtBolletje(self) {
  try { return self.ketBeurt() ? '1' : ''; } catch (e) { return ''; }
}

/**
 * Heeft het beestje je nodig? Dat is zo als zijn buik of zijn humeur onder een
 * kwart staat, en alleen als hij uit het ei is.
 *
 * In een eigen functie en met een vangnet eromheen: dit rekent op de spelregels
 * uit lib/tamagotchi.js, en een taakbalk mag nooit omvallen omdat een spelletje
 * iets geks teruggeeft.
 */
function tamRoept(self) {
  try {
    const b = self.state.tamBeest;
    if (!b) return false;
    const st = tam.standVan(b, Date.now(), new Date().getHours());
    return st.fase !== 'ei' && !st.slaapt && (st.honger < 25 || st.blij < 25);
  } catch (e) { return false; }
}

/** Hoeveel ideetjes er zijn bijgekomen sinds je laatste bezoek; leeg als het er geen zijn. */
function nieuweIdeetjes(self) {
  try { const n = self.nieuweIdeetjes(); return n > 0 ? String(n) : ''; } catch (e) { return ''; }
}

/** Hoeveel verzoekjes er op je liggen te wachten; leeg als het er geen zijn. */
function ongelezenVerzoeken(self) {
  const n = (self.state.reqList || []).filter((r) => r.status === 'open' && r.toId === self.state.accountId).length;
  return n > 0 ? String(n) : '';
}
